import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import useDocumentTitle from "@/lib/useDocumentTitle";
import {
  Flame, ShieldCheck, CheckCircle2, Clock, ArrowRight, Phone, AlertTriangle,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1560170412-0f7df0eb0fb1?w=1920&h=1080&fit=crop&q=85";

/**
 * Single-page conversion-optimized landing for paid traffic (Google Ads / Meta).
 * Stripped chrome (no navbar/footer chrome distractions), one primary CTA
 * repeated, urgency framing, trust signals, no secondary nav links.
 */
export default function AdsLanding() {
  const [content, setContent] = useState(null);

  useDocumentTitle(
    "Stop Foreclosure in 60 Days — Azure Gypsy Guild Counterclaim Relief",
    "Private trustees file your counterclaim, audit the ledger, and settle in 60 days. 98% success rate. No fee until settlement.",
  );

  useEffect(() => {
    api.get("/content/landing").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-paper text-[color:var(--text-primary)]" data-testid="ads-landing">
      {/* Minimal top bar — logo + phone CTA only, no nav links to leak focus. */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="ads-logo">
            <span className="h-6 w-6 rounded-md bg-[color:var(--fire-red)] flex items-center justify-center text-white">
              <Flame className="h-3.5 w-3.5" />
            </span>
            <strong className="font-serif">Azure Gypsy Guild</strong>
          </Link>
          <a href="#apply" className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[color:var(--fire-red)] hover:text-[color:var(--fire-red-hover)]" data-testid="ads-top-cta">
            Free case review <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/82 via-black/65 to-black/45" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 py-20 sm:py-28">
          <Badge className="bg-[color:var(--fire-red)] text-white px-3 py-1 uppercase tracking-[0.2em] text-[10px]" data-testid="ads-hero-eyebrow">
            <AlertTriangle className="h-3 w-3 mr-1" /> Foreclosure relief — 60-day timeline
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mt-5 leading-[1.05] max-w-3xl" data-testid="ads-hero-headline">
            Stop foreclosure. <span className="text-[color:var(--orange)]">Keep your home.</span> In 60 days.
          </h1>
          <p className="text-white/90 text-lg sm:text-xl mt-5 max-w-2xl leading-relaxed" data-testid="ads-hero-sub">
            Private trustees file a counterclaim against the bank, pledge funds into your trustee account,
            and force settlement through a verified accounting ledger audit. <strong>No fee until your case settles.</strong>
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white text-base" data-testid="ads-hero-cta-primary">
              <a href="#apply"><Flame className="mr-2 h-5 w-5" />Get my free case review</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white text-base" data-testid="ads-hero-cta-secondary">
              <a href="tel:+1-000-000-0000"><Phone className="mr-2 h-5 w-5" />Speak to a trustee</a>
            </Button>
          </div>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-3 max-w-xl gap-6 text-white" data-testid="ads-hero-trust">
            <div>
              <div className="font-serif text-3xl sm:text-4xl text-[color:var(--bright-green)]">98%</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1">Settlement rate</div>
            </div>
            <div>
              <div className="font-serif text-3xl sm:text-4xl text-[color:var(--orange)]">60</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1">Days to relief</div>
            </div>
            <div>
              <div className="font-serif text-3xl sm:text-4xl text-[color:var(--fire-red)]">1.5%</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1">Fee at settlement</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 STEPS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="max-w-2xl">
            <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--fire-red)]">How it works</span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2">Three steps. One outcome — your home stays yours.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-10" data-testid="ads-steps">
            {[
              { i: 1, t: "Apply in 10 minutes", d: "Tell us about your mortgage, your lender, and your court status." },
              { i: 2, t: "Trustees take over", d: "We file the counterclaim, audit the ledger, and invoke mediation/arbitration." },
              { i: 3, t: "Settlement & relief", d: "Foreclosure halts. Mortgage is reassigned. Tracking number issued." },
            ].map((s) => (
              <Card key={s.i} className="border-black/5">
                <CardContent className="p-6">
                  <div className="font-serif text-4xl text-[color:var(--orange)]">0{s.i}</div>
                  <h3 className="font-serif text-xl mt-2">{s.t}</h3>
                  <p className="text-sm text-[color:var(--text-secondary)] mt-2 leading-relaxed">{s.d}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF — testimonial + checklist */}
      <section className="py-16 sm:py-20 bg-[color:var(--bg-secondary)]">
        <div className="max-w-5xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--fire-red)]">Why members trust the Guild</span>
            <h2 className="font-serif text-3xl mt-2">Every step is signed, audited, and tracked.</h2>
            <ul className="mt-6 space-y-3 text-[color:var(--text-secondary)] text-sm" data-testid="ads-checklist">
              {[
                "Six signed e-agreements (NDA, membership, moral clause, civil rights, mediation)",
                "Forensic accounting ledger audit of your mortgage and tax record",
                "Counterclaim filed in court, served to law firm and bank",
                "Trustee pledge into the trustee account — mortgage reassigned",
                "Manual case tracking number from day one — see every step in your portal",
                "Fee paid only at settlement — no settlement, no fee",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[color:var(--bright-green)] mt-0.5 flex-shrink-0" />
                  <span className="text-[color:var(--text-primary)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <Card className="border-black/5">
            <CardContent className="p-8">
              <ShieldCheck className="h-6 w-6 text-[color:var(--fire-red)]" />
              <blockquote className="font-serif text-xl mt-3 leading-snug" data-testid="ads-testimonial">
                &ldquo;Azure Gypsy Guild stopped my foreclosure in 48 days. The trustees walked me through every step.&rdquo;
              </blockquote>
              <p className="text-xs text-[color:var(--text-secondary)] mt-3">— Marcus W., Atlanta, GA</p>
              <div className="mt-6 border-t border-black/5 pt-5 flex items-center gap-3 text-xs text-[color:var(--text-secondary)]">
                <Clock className="h-4 w-4" />
                <span>Average case resolution: <strong className="text-[color:var(--text-primary)]">48-58 days</strong></span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section id="apply" className="py-20 sm:py-24 bg-[#0c0e0c]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Badge className="bg-[color:var(--orange)] text-white px-3 py-1 uppercase tracking-[0.2em] text-[10px]">
            Free case review
          </Badge>
          <h2 className="font-serif text-4xl sm:text-5xl text-white mt-4 leading-tight" data-testid="ads-cta-headline">
            Find out if your case qualifies — free, no obligation.
          </h2>
          <p className="text-white/70 mt-5 text-lg leading-relaxed">
            Submit your application in 10 minutes. A trustee reviews within one business day. Priority handling
            for cases with a sheriff sale already scheduled.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white text-base" data-testid="ads-bottom-cta-primary">
              <Link to="/register"><Flame className="mr-2 h-5 w-5" />Start my free case review</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white text-base" data-testid="ads-bottom-cta-secondary">
              <Link to="/accounting">How the 1.5% works <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-10">
            Nothing on this page is legal, tax, or financial advice. Azure Gypsy Guild is a private members guild.
          </p>
        </div>
      </section>
    </div>
  );
}
